import { ApiClient } from './apiClient'
import { authService } from './authService'
import type { Team, TeamCreationPayload } from '@/types/team'
import type { TeamRole, TeamUser, TeamUserAddPayload } from '@/types/teamUser'
import type { User } from '@/types/user'

// A session of a team the user is an active member of - the slim shape the
// GET /teams/:id/users/:uid/sessions endpoint returns for the removal form.
export interface UserSessionMembership {
  id: string
  phase: number
  endedAt: string
  questionnaireEndedAt: string
  createdAt: string
}

// The membership endpoints now return plain users sourced from user_entities
// (the team_users join table is gone). The Go User struct serialises the name
// fields as camelCase (`json:"firstName"`) while everything else is
// snake_case - tolerate both, mirroring userService.fetchById.
const mapWireUser = (u: any): User => ({
  id: u.id,
  email: u.email,
  firstName: u.first_name ?? u.firstName ?? null,
  lastName: u.last_name ?? u.lastName ?? null,
  language: u.language,
  createdAt: u.created_at ?? u.createdAt,
  updatedAt: u.updated_at ?? u.updatedAt,
})

// A member's team role now lives in RBAC (context_user_roles), not in the
// roster payload. When a user somehow holds more than one team-scoped role,
// surface the most privileged one - same precedence the backend uses.
const TEAM_ROLE_PRIORITY: TeamRole[] = ['team_leader', 'scrum_master', 'team_member']
const primaryTeamRole = (roles: string[] | undefined): TeamRole =>
  TEAM_ROLE_PRIORITY.find((r) => roles?.includes(r)) ?? 'team_member'

class TeamService extends ApiClient {
  async create(team: Partial<TeamCreationPayload>): Promise<Team> {
    const response = await this.post<Team>('/teams/', team, { version: 'v2' })
    return response.data
  }

  // Rename a team. Hits the v2 endpoint, which is role-gated to
  // tpa_admin / org_leader and confines an org_leader to their own
  // organization. The v1 twin (PUT /v1/teams/:id) is ungated and full-saves
  // the team - do not switch this back to it.
  async update(teamId: string, name: string): Promise<void> {
    await this.put(`/teams/${teamId}`, { name }, { version: 'v2' })
  }

  async activate(teamId: string): Promise<void> {
    await this.post(`/teams/${teamId}/activate`, undefined, { version: 'v2' })
  }

  async deactivate(teamId: string): Promise<void> {
    await this.post(`/teams/${teamId}/deactivate`, undefined, { version: 'v2' })
  }

  async fetchAll(): Promise<Team[]> {
    const response = await this.get<Team[]>('/teams')
    return response.data
  }

  async fetchById(teamId: string): Promise<Team> {
    const response = await this.get<Team>(`/teams/${teamId}`)
    return response.data
  }

  async fetchByIDs(teamIds: string[]): Promise<Team[]> {
    if (!teamIds || teamIds.length === 0) return []
    const response = await this.get<Team[]>('/teams', {
      params: { ids: teamIds.join(',') },
    })
    return response.data
  }

  async fetchWithSessions(): Promise<any[]> {
    const response = await this.get<any[]>('/teams/with-sessions')
    return response.data.map((item: any) => ({
      ...item,
      current_session: item.current_session
        ? {
            id: item.current_session.id,
            phase: item.current_session.phase,
            questionnaire_started_at: item.current_session.questionnaire_started_at,
            questionnaire_ended_at: item.current_session.questionnaire_ended_at,
            started_at: item.current_session.started_at,
            ended_at: item.current_session.ended_at,
            results_checked: item.current_session.results_checked,
            feedback_requested: item.current_session.feedback_requested,
            advice: item.current_session.advice || null,
          }
        : null,
      response_count: item.response_count ?? 0,
      total_members: item.total_members ?? 0,
    }))
  }

  // Team roster. GET /teams/{id}/users returns the members as plain users
  // (sourced from user_entities via the session chain); each member's team
  // role is resolved with one batched RBAC lookup. The result keeps the
  // TeamUser shape every consumer already renders (userId + role + user).
  async fetchTeamUsers(teamId: string): Promise<TeamUser[]> {
    const response = await this.get<any[]>(`/teams/${teamId}/users`)
    const users = (response.data ?? []).map(mapWireUser)

    let rolesByUser: Record<string, string[]> = {}
    try {
      rolesByUser = await authService.getRBACRolesBatch(
        users.map((u) => u.id),
        { params: { entity: 'teams', entityId: teamId } },
      )
    } catch (err) {
      // Roles are presentation data - degrade to the default role rather
      // than failing the whole roster fetch.
      console.error('Failed to load team RBAC roles for team', teamId, err)
    }

    return users.map(
      (user): TeamUser => ({
        // Synthetic row id - the team_users row (and its id) no longer exists.
        id: `${teamId}:${user.id}`,
        teamId,
        userId: user.id,
        role: primaryTeamRole(rolesByUser[user.id]),
        user,
      }),
    )
  }

  async fetchUsers(teamId: string): Promise<TeamUser[]> {
    return this.fetchTeamUsers(teamId)
  }

  async addMembers(teamId: string, userEmails: string[]): Promise<void> {
    await this.post(`/teams/${teamId}/users`, { emails: userEmails })
  }

  async removeMember(teamId: string, userId: string): Promise<void> {
    await this.delete(`/teams/${teamId}/users/${userId}`,{version: 'v2'})
  }

  async replaceLeader(teamId: string, newLeaderEmail: string): Promise<void> {
    await this.put(`/teams/${teamId}/replace_leader`, { email: newLeaderEmail }, { version: 'v2' })
  }

  async moveToNextSession(teamId: string): Promise<void> {
    await this.patch(`/teams/${teamId}/sessions/next`)
  }

  // The sessions of `teamId` that `userId` is an active member of. Drives the
  // OrganizationView removal form (team dropdown -> session checkboxes).
  async fetchUserSessions(teamId: string, userId: string): Promise<UserSessionMembership[]> {
    const response = await this.get<any[]>(`/teams/${teamId}/users/${userId}/sessions`, {
      version: 'v2',
    })
    return (response.data ?? []).map((s: any): UserSessionMembership => ({
      id: s.id,
      phase: s.phase ?? 0,
      endedAt: s.ended_at ?? '',
      questionnaireEndedAt: s.questionnaire_ended_at ?? '',
      createdAt: s.created_at ?? '',
    }))
  }

  async addTeamMembers(teamId: string, userEmails: string[]): Promise<void> {
    await this.post(`/teams/${teamId}/users`, { emails: userEmails })
  }

  async addMember(teamUser: TeamUserAddPayload): Promise<void> {
    await this.post(
      `/teams/users/add`,
      teamUser,
      { version: 'v2' },
    )
  }

  async updateTeamUserRole(
    teamId: string,
    userId: string,
    newRole: string,
  ): Promise<void> {
    await this.patch(`/teams/${teamId}/users/${userId}`, { role: newRole })
  }

}

export const teamService = new TeamService()

